// src/api/axiosInstance.js
import axios from 'axios';
import { refreshToken as refreshTokenApi } from './api/authApi';

const API_URL = `${process.env.REACT_APP_API_URL}`;

// Create an instance of Axios
export const axiosInstance = axios.create({
  baseURL: API_URL,
  // timeout: 10000,    // Timeout for requests
});

const setAuthHeader = (config, token) => {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
};

export const clearSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('emailId');
  localStorage.removeItem('loginType');
};

// '/' is the login route (see App.js). Guarded so a burst of 401s from
// concurrent requests can't reload the login page on top of itself.
const redirectToLogin = () => {
  clearSession();
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// While a refresh is in flight, every other 401 waits on this promise instead of
// firing its own refresh call.
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = refreshTokenApi(storedRefreshToken)
    .then((response) => {
      const data = response?.data?.data || {};
      const newAccessToken = data.token || data.accessToken;

      if (!newAccessToken) {
        throw new Error('Refresh response did not contain an access token');
      }

      localStorage.setItem('accessToken', newAccessToken);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      return newAccessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const isAuthError = (error) => {
  const status = error.response?.status;
  return status === 401 || error.response?.data?.msg === 'User not authorised';
};

// Endpoints reachable without a session. A 401 here means "bad credentials",
// not "expired token", so it must surface to the caller rather than trigger a
// refresh and bounce the user to the login page.
const PUBLIC_ENDPOINTS = [
  'auth/admin-login',
  'auth/admin-refresh-token',
  'reset-password/',
];

const isPublicEndpoint = (config) => {
  const url = config?.url || '';
  return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

// Request Interceptor
const attachToken = (config) => {
  const token = localStorage.getItem('accessToken'); // Adding token to headers
  if (token) {
    setAuthHeader(config, token);
  }
  return config;
};

// Response Interceptor
const handleResponseError = (client) => async (error) => {
  const originalRequest = error.config;

  if (!error.response) {
    console.log('Network error');
    return Promise.reject(error);
  }

  if (isPublicEndpoint(originalRequest)) {
    return Promise.reject(error);
  }

  if (isAuthError(error) && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();
      setAuthHeader(originalRequest, newAccessToken);
      return client(originalRequest);
    } catch (refreshError) {
      console.log('Session expired, please login');
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  }

  if (error.response.status === 401 || error.response.status === 403) {
    console.log('Unauthorized, please login');
    redirectToLogin();
  } else if (error.response.status === 500) {
    console.log('Internal server error');
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.request.use(attachToken, (error) => Promise.reject(error));
axiosInstance.interceptors.response.use(
  (response) => response,
  handleResponseError(axiosInstance)
);

// The API modules that call `axios` directly (rather than this instance) set
// their own Authorization header, but still need refresh-and-retry on 401.
axios.interceptors.response.use(
  (response) => response,
  handleResponseError(axios)
);

export default axiosInstance;
